import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { campaignsApi } from '@/lib/api'
import { useClientPreset } from '@/contexts/ClientPresetContext'
import { campaignTypes } from '@/data/campaignTypes'
import toast from 'react-hot-toast'

interface CampaignForm {
  name: string
  budget: number
  start_date: string
  end_date: string
  landing_url: string
  campaign_type: string
  roas_target?: number
}

export default function CampaignPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [launching, setLaunching] = useState(false)
  const { selectedPreset } = useClientPreset()
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm<CampaignForm>({
    defaultValues: {
      name: `Campaign ${new Date().toLocaleDateString()}`,
      budget: 1000,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      landing_url: 'https://martimart.com',
      campaign_type: 'conversions', // Default to conversions
      roas_target: 1.6
    }
  })

  const budget = watch('budget')
  const campaignType = watch('campaign_type')
  const roasTarget = watch('roas_target')
  const approvedVariants = location.state?.approvedVariants || []

  // Get selected campaign type details
  const selectedCampaignType = campaignTypes.find(type => type.id === campaignType)

  const onSubmit = async (data: CampaignForm) => {
    if (approvedVariants.length === 0) {
      toast.error('No approved variants selected')
      return
    }

    if (!selectedPreset) {
      toast.error('Please select a client first')
      return
    }

    setLaunching(true)
    try {
      const launchData = {
        name: data.name,
        budget: data.budget * 100, // Convert to cents
        start: data.start_date,
        end: data.end_date,
        variant_ids: approvedVariants,
        client_id: parseInt(selectedPreset.id),
        utmTemplate: selectedPreset.utmTemplate,
        campaign_type: data.campaign_type,
        objective: selectedCampaignType?.objective,
        optimization_goal: selectedCampaignType?.optimizationGoal,
        // Include ROAS target for ROAS campaigns
        ...(data.campaign_type === 'roas' && { roas_target: data.roas_target }),
      }

      const response = await campaignsApi.launch(launchData)

      toast.success('Campaign launched successfully!')
      navigate(`/dashboard/${response.data.campaign_id}`)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to launch campaign')
    } finally {
      setLaunching(false)
    }
  }

  return (
    <div style={{
      width: '100%',
      padding: 'var(--spacing-8) 0',
      fontFamily: 'var(--font-family-appleSystem)',
    }}>
      <div style={{
        background: 'var(--color-card)',
        borderRadius: '24px',
        boxShadow: 'var(--shadow-appleModal)',
        border: '1px solid var(--color-border)',
        padding: 'var(--spacing-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-6)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 700,
            color: 'var(--color-label)',
            marginBottom: 'var(--spacing-2)',
            letterSpacing: 'var(--letter-spacing-tight)',
          }}>
            Launch Campaign
          </h2>
          <p style={{
            color: 'var(--color-secondaryLabel)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            marginBottom: 'var(--spacing-6)',
          }}>
            Configure your Meta advertising campaign with approved variants
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {/* Campaign Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <label htmlFor="name" style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              textAlign: 'left',
            }}>
              Campaign Name
            </label>
            <input
              type="text"
              {...register('name', { required: 'Campaign name is required' })}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-systemBackground)',
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-base)',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
            />
            {errors.name && (
              <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Budget */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <label htmlFor="budget" style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              textAlign: 'left',
            }}>
              Lifetime Budget (USD)
            </label>
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-base)',
                pointerEvents: 'none',
              }}>
                $
              </div>
              <input
                type="number"
                min="10"
                max="10000"
                step="10"
                {...register('budget', { 
                  required: 'Budget is required',
                  min: { value: 10, message: 'Minimum budget is $10' },
                  max: { value: 10000, message: 'Maximum budget is $10,000' }
                })}
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 36px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>
            {errors.budget && (
              <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                {errors.budget.message}
              </p>
            )}
            <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
              Budget remaining after fees: ${(budget * 0.95).toFixed(2)}
            </p>
          </div>

          {/* Date Range */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
              <label htmlFor="start_date" style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                textAlign: 'left',
              }}>
                Start Date
              </label>
              <input
                type="date"
                {...register('start_date', { required: 'Start date is required' })}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
              {errors.start_date && (
                <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                  {errors.start_date.message}
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
              <label htmlFor="end_date" style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                textAlign: 'left',
              }}>
                End Date
              </label>
              <input
                type="date"
                {...register('end_date', { required: 'End date is required' })}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
              {errors.end_date && (
                <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                  {errors.end_date.message}
                </p>
              )}
            </div>
          </div>

          {/* Landing URL */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <label htmlFor="landing_url" style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              textAlign: 'left',
            }}>
              Landing Page URL
            </label>
            <input
              type="url"
              {...register('landing_url', { 
                required: 'Landing URL is required',
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Please enter a valid URL'
                }
              })}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-systemBackground)',
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-base)',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              placeholder="https://martimart.com"
            />
            {errors.landing_url && (
              <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                {errors.landing_url.message}
              </p>
            )}
          </div>

          {/* Campaign Type */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <label htmlFor="campaign_type" style={{
              color: 'var(--color-secondaryLabel)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              textAlign: 'left',
            }}>
              Campaign Type
            </label>
            <select
              {...register('campaign_type', { required: 'Campaign type is required' })}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-systemBackground)',
                color: 'var(--color-label)',
                fontSize: 'var(--font-size-base)',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
            >
              {campaignTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
            {errors.campaign_type && (
              <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                {errors.campaign_type.message}
              </p>
            )}
            {selectedCampaignType && (
              <p style={{ 
                color: 'var(--color-secondaryLabel)', 
                fontSize: 'var(--font-size-sm)', 
                marginTop: 4,
                fontStyle: 'italic'
              }}>
                {selectedCampaignType.description}
              </p>
            )}
          </div>

          {/* ROAS Target - Only show for ROAS campaigns */}
          {campaignType === 'roas' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
              <label htmlFor="roas_target" style={{
                color: 'var(--color-secondaryLabel)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                textAlign: 'left',
              }}>
                Target ROAS (Return on Ad Spend)
                <span style={{ 
                  fontSize: 'var(--font-size-xs)', 
                  color: 'var(--color-secondaryLabel)',
                  fontWeight: 'normal',
                  marginLeft: 'var(--spacing-2)'
                }}>
                  Example: 3.0 = $3 revenue for every $1 spent
                </span>
              </label>
              <input
                type="number"
                min="1.0"
                max="20.0"
                step="0.1"
                {...register('roas_target', { 
                  required: campaignType === 'roas' ? 'ROAS target is required for ROAS campaigns' : false,
                  min: { value: 1.0, message: 'Minimum ROAS target is 1.0' },
                  max: { value: 20.0, message: 'Maximum ROAS target is 20.0' }
                })}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                placeholder="1.6"
              />
              {errors.roas_target && (
                <p style={{ color: 'var(--color-systemRed)', fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                  {errors.roas_target.message}
                </p>
              )}
              <div style={{
                marginTop: 'var(--spacing-2)',
                padding: 'var(--spacing-3)',
                background: 'var(--color-primaryTranslucent)',
                borderRadius: '8px',
                border: '1px solid var(--color-primaryBorder)'
              }}>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-label)',
                  margin: 0,
                  lineHeight: 'var(--line-height-normal)'
                }}>
                  💡 <strong>ROAS Target:</strong> Meta will optimize your campaign to achieve this return on ad spend. 
                  Higher targets may limit reach but improve profitability. Start with 1.6-4.0 for most businesses.
                </p>
              </div>
            </div>
          )}

          {/* Campaign Summary */}
          <div style={{
            background: 'var(--color-tertiarySystemBackground)',
            borderRadius: '16px',
            padding: 'var(--spacing-5)',
            marginTop: 'var(--spacing-4)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              color: 'var(--color-label)',
              marginBottom: 'var(--spacing-3)',
            }}>
              Campaign Summary
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--spacing-3)',
              fontSize: 'var(--font-size-sm)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-secondaryLabel)' }}>Ad Variants:</span>
                <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>{approvedVariants.length}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-secondaryLabel)' }}>Campaign Type:</span>
                <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>{selectedCampaignType?.name || 'Not selected'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-secondaryLabel)' }}>Objective:</span>
                <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>{selectedCampaignType?.objective || 'Not set'}</span>
              </div>
              {campaignType === 'roas' && roasTarget && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-secondaryLabel)' }}>ROAS Target:</span>
                  <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>{roasTarget}x</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-secondaryLabel)' }}>Targeting:</span>
                <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>US, Ages 18-65</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-secondaryLabel)' }}>Platform:</span>
                <span style={{ color: 'var(--color-label)', fontWeight: 500 }}>Facebook & Instagram</span>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            marginTop: 'var(--spacing-6)',
          }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                flex: 1,
                padding: '14px 0',
                borderRadius: '12px',
                background: 'var(--color-systemBackground)',
                color: 'var(--color-label)',
                fontWeight: 600,
                fontSize: 'var(--font-size-base)',
                border: '1px solid var(--color-border)',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={launching || approvedVariants.length === 0}
              style={{
                flex: 2,
                padding: '14px 0',
                borderRadius: '12px',
                background: launching || approvedVariants.length === 0 
                  ? 'var(--color-tertiaryLabel)' 
                  : 'var(--color-primary)',
                color: 'var(--color-systemBackground)',
                fontWeight: 600,
                fontSize: 'var(--font-size-base)',
                border: 'none',
                boxShadow: 'var(--shadow-button)',
                cursor: launching || approvedVariants.length === 0 ? 'not-allowed' : 'pointer',
                opacity: launching || approvedVariants.length === 0 ? 0.7 : 1,
                transition: 'background 0.2s, opacity 0.2s',
              }}
            >
              {launching ? 'Launching...' : 'Launch Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
} 